
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, CheckCircle, Users, TrendingUp, PiggyBank } from "lucide-react";

interface CoApplicantInfoPopupProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  language?: string;
}

export const CoApplicantInfoPopup = ({ 
  open, 
  setOpen,
  language = "fi" 
}: CoApplicantInfoPopupProps) => {
  
  const getTitle = () => {
    if (language === "sv") return "Fördelar med en medansökande";
    if (language === "en") return "Benefits of a Co-Applicant";
    if (language === "ar") return "فوائد مقدم الطلب المشترك";
    if (language === "he") return "יתרונות של מבקש משותף";
    return "Rinnakkaishakijan edut";
  };
  
  const getDescription = () => {
    if (language === "sv") {
      return "En medansökande kan förbättra dina chanser att få lån och bättre ränta";
    }
    if (language === "en") {
      return "A co-applicant can improve your chances of loan approval and better interest rates";
    }
    if (language === "ar") {
      return "يمكن أن يحسن مقدم الطلب المشترك فرصك في الموافقة على القرض وأسعار فائدة أفضل";
    }
    if (language === "he") {
      return "מבקש משותף יכול לשפר את הסיכויים שלך לקבלת אישור הלוואה וריבית טובה יותר";
    }
    return "Rinnakkaishakija voi parantaa lainahakemuksesi hyväksymismahdollisuuksia ja lainaehtoja";
  };

  const isRtl = language === "ar" || language === "he";
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className={`sm:max-w-md ${isRtl ? 'rtl' : ''}`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-blue-700 flex items-center">
            <Users className="mr-2 h-5 w-5 text-blue-600" />
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-gradient-to-r from-blue-50 to-white p-4 rounded-lg border border-blue-100 my-2">
          <div className="space-y-4">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800">
                  {language === "en" ? "Higher loan approval chances" : 
                   language === "sv" ? "Större chans till godkännande" :
                   language === "ar" ? "فرص أعلى للموافقة على القرض" :
                   language === "he" ? "סיכויי אישור הלוואה גבוהים יותר" :
                   "Paremmat mahdollisuudet saada laina"}
                </p>
                <p className="text-sm text-gray-600">
                  {language === "en" ? "Two incomes provide more security for lenders" : 
                   language === "sv" ? "Två inkomster ger mer säkerhet för långivare" :
                   language === "ar" ? "دخلان يوفران مزيدًا من الأمان للمقرضين" :
                   language === "he" ? "שתי הכנסות מספקות יותר ביטחון למלווים" :
                   "Kaksi tulonsaajaa tarjoaa enemmän varmuutta lainanantajille"}
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <TrendingUp className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800">
                  {language === "en" ? "Better interest rates" : 
                   language === "sv" ? "Bättre räntor" :
                   language === "ar" ? "أسعار فائدة أفضل" :
                   language === "he" ? "שיעורי ריבית טובים יותר" :
                   "Parempi korkotarjous"}
                </p>
                <p className="text-sm text-gray-600">
                  {language === "en" ? "Lower interest offers for lower risk applications" : 
                   language === "sv" ? "Lägre räntor för ansökningar med lägre risk" :
                   language === "ar" ? "عروض فائدة أقل للطلبات ذات المخاطر المنخفضة" :
                   language === "he" ? "הצעות ריבית נמוכות יותר עבור בקשות בסיכון נמוך" :
                   "Pienempi riski lainanantajalle voi tarkoittaa alhaisempaa korkoa"}
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <PiggyBank className="h-5 w-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800">
                  {language === "en" ? "Higher loan amounts possible" : 
                   language === "sv" ? "Högre lånebelopp möjliga" :
                   language === "ar" ? "إمكانية الحصول على مبالغ قروض أعلى" :
                   language === "he" ? "סכומי הלוואה גבוהים יותר אפשריים" :
                   "Mahdollisuus suurempaan lainasummaan"}
                </p>
                <p className="text-sm text-gray-600">
                  {language === "en" ? "Combined income can qualify for larger loans" : 
                   language === "sv" ? "Kombinerad inkomst kan kvalificera för större lån" :
                   language === "ar" ? "يمكن أن يؤهلك الدخل المشترك للحصول على قروض أكبر" :
                   language === "he" ? "הכנסה משולבת יכולה להיות זכאית להלוואות גדולות יותר" :
                   "Yhdistetyt tulot mahdollistavat suuremman lainan hakemisen"}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end">
          <DialogClose asChild>
            <Button>Sulje</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
};
